'use client';
import React, { useEffect, useRef } from 'react';
import Link from 'next/link';
import { Sun, Coffee, Smartphone, BarChart2, Moon, ArrowRight, Sparkles, Mic, CheckCircle, Clock, MessageSquare, TrendingUp, Bell, Zap, Play, RefreshCw, Star } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

const timelineItems = [
  {
    time: '7:00 AM',
    emoji: '🌅',
    title: 'Morning Brief',
    icon: Sun,
    color: '#00D4FF',
    glow: 'rgba(0,212,255,0.20)',
    gradient: 'linear-gradient(135deg, #00D4FF, #0099CC)',
    story: "You open AgentOS. Before you've had your coffee, your day is already organized.",
    detail: "AgentOS has already reviewed your calendar, contacts, CRM activity, emails, market changes, and overnight activity. It tells you exactly what matters.",
    bullets: [
      'Who needs attention today',
      'What changed overnight',
      'Which buyers are heating up',
      'Which listings need action',
      'What your day should look like',
    ],
    mockup: {
      type: 'morning-brief',
    },
  },
  {
    time: '10:30 AM',
    emoji: '🏡',
    title: 'Between Appointments',
    icon: Smartphone,
    color: '#A78BFA',
    glow: 'rgba(167,139,250,0.20)',
    gradient: 'linear-gradient(135deg, #A78BFA, #7C3AED)',
    story: "You have 8 minutes between showings. You open AgentOS and ask one question.",
    detail: 'AgentOS doesn\'t just give you a name. It explains why — with confidence, recommended action, and one-click follow-up ready to go.',
    bullets: [
      'Confidence score for each lead',
      'Clear reason why they need attention',
      'Recommended next action',
      'One-click follow-up ready',
    ],
    mockup: {
      type: 'who-to-call',
    },
  },
  {
    time: '12:30 PM',
    emoji: '☕',
    title: 'Catch Me Up',
    icon: Coffee,
    color: '#22C55E',
    glow: 'rgba(34,197,94,0.20)',
    gradient: 'linear-gradient(135deg, #22C55E, #16A34A)',
    story: "You sit down for lunch. A lot happened this morning. You tap one button.",
    detail: "AgentOS summarizes everything since your morning briefing — in plain language, in under 60 seconds.",
    bullets: [
      'Email replies received',
      'New listings that match your buyers',
      'Buyer activity and engagement',
      'Schedule changes',
      'Completed tasks',
      'Recently created drafts',
    ],
    mockup: {
      type: 'catch-me-up',
    },
  },
  {
    time: '2:30 PM',
    emoji: '📱',
    title: 'Marketing',
    icon: Star,
    color: '#F59E0B',
    glow: 'rgba(245,158,11,0.20)',
    gradient: 'linear-gradient(135deg, #F59E0B, #D97706)',
    story: "You need to post something today. You ask AgentOS to handle it.",
    detail: "AgentOS already understands your listings, your audience, and your market. It generates a beautiful post — ready for one-click approval.",
    bullets: [
      'Knows your active listings',
      'Understands your audience',
      'Matches your brand voice',
      'One-click approval and post',
    ],
    mockup: {
      type: 'marketing',
    },
  },
  {
    time: '4:30 PM',
    emoji: '📊',
    title: 'Business Dashboard',
    icon: BarChart2,
    color: '#00D4FF',
    glow: 'rgba(0,212,255,0.20)',
    gradient: 'linear-gradient(135deg, #00D4FF, #A78BFA)',
    story: "You want to see how the month is tracking. You click one button.",
    detail: "The Business Dashboard gives you a complete view of your pipeline, activity, and performance. Switch between AI-first and traditional dashboard views at any time.",
    bullets: [
      'Pipeline health at a glance',
      'Deals advanced this week',
      'Follow-up completion rate',
      'Switch between AI and traditional view',
    ],
    mockup: {
      type: 'dashboard',
    },
  },
  {
    time: '6:00 PM',
    emoji: '🌙',
    title: 'Wrap Up',
    icon: Moon,
    color: '#A78BFA',
    glow: 'rgba(167,139,250,0.20)',
    gradient: 'linear-gradient(135deg, #A78BFA, #7C3AED)',
    story: "Before you close your laptop, you tap Wrap Up. You leave feeling completely organized.",
    detail: "AgentOS summarizes your entire day — what you accomplished, what's still pending, and exactly what to do first tomorrow.",
    bullets: [
      'Completed work today',
      'Pending items and follow-ups',
      'Deals advanced',
      'Deals needing attention',
      "Tomorrow\'s priorities",
      "Tomorrow\'s calendar preview",
      'Prepared emails ready to send',
      'Recommended first task tomorrow',
    ],
    mockup: {
      type: 'wrap-up',
    },
  },
];

function MorningBriefMockup() {
  return (
    <div className="rounded-2xl overflow-hidden" style={{ background: '#0B0F19', border: '1px solid rgba(0,212,255,0.20)', boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }}>
      <div className="flex items-center gap-2 px-4 py-3" style={{ background: 'rgba(0,212,255,0.06)', borderBottom: '1px solid rgba(0,212,255,0.12)' }}>
        <div className="w-2.5 h-2.5 rounded-full" style={{ background: '#ff5f57' }} />
        <div className="w-2.5 h-2.5 rounded-full" style={{ background: '#febc2e' }} />
        <div className="w-2.5 h-2.5 rounded-full" style={{ background: '#28c840' }} />
        <span className="ml-2 text-xs font-medium" style={{ color: 'rgba(0,212,255,0.70)', fontFamily: 'JetBrains Mono, monospace' }}>Morning Brief — 7:00 AM</span>
        <div className="ml-auto flex items-center gap-1.5">
          <div className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
          <span className="text-xs text-green-400" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Live</span>
        </div>
      </div>
      <div className="p-5 space-y-3">
        <div className="rounded-xl p-4" style={{ background: 'rgba(0,212,255,0.07)', border: '1px solid rgba(0,212,255,0.15)' }}>
          <div className="flex items-center gap-2 mb-2">
            <Sun size={13} style={{ color: '#00D4FF' }} />
            <span className="text-xs font-bold" style={{ color: '#00D4FF', fontFamily: 'JetBrains Mono, monospace' }}>GOOD MORNING, SARAH</span>
          </div>
          <p className="text-xs leading-relaxed" style={{ color: 'rgba(240,244,255,0.80)' }}>
            You have 4 follow-ups due today. Marcus Webb viewed 3 listings last night — he's your hottest lead. The Petersons are waiting on your offer response. Two new listings hit your target zip code overnight.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-2">
          {[
            { label: 'Follow-ups due', value: '4', color: '#00D4FF' },
            { label: 'Hot leads', value: '2', color: '#A78BFA' },
            { label: 'New listings', value: '2', color: '#22C55E' },
            { label: 'Offers pending', value: '1', color: '#F59E0B' },
          ].map((stat) => (
            <div key={stat.label} className="rounded-lg p-3" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)' }}>
              <div className="text-lg font-extrabold mb-0.5" style={{ color: stat.color, fontFamily: 'JetBrains Mono, monospace' }}>{stat.value}</div>
              <div className="text-xs" style={{ color: 'rgba(240,244,255,0.45)' }}>{stat.label}</div>
            </div>
          ))}
        </div>
        <div className="rounded-xl p-3" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
          <div className="flex items-center gap-2 mb-2">
            <div className="w-5 h-5 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #00D4FF, #0099CC)' }}>
              <Mic size={9} className="text-black" />
            </div>
            <span className="text-xs italic" style={{ color: 'rgba(240,244,255,0.50)' }}>"What should I focus on first?"</span>
          </div>
          <p className="text-xs leading-relaxed" style={{ color: 'rgba(240,244,255,0.75)', background: 'rgba(0,212,255,0.07)', padding: '8px 10px', borderRadius: '8px', border: '1px solid rgba(0,212,255,0.12)' }}>
            Call Marcus Webb first — he's been active overnight. Then review the Peterson offer before 10 AM. Your calendar is clear until 11.
          </p>
        </div>
      </div>
    </div>
  );
}

function WhoToCallMockup() {
  return (
    <div className="rounded-2xl overflow-hidden" style={{ background: '#0B0F19', border: '1px solid rgba(167,139,250,0.20)', boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }}>
      <div className="flex items-center gap-2 px-4 py-3" style={{ background: 'rgba(167,139,250,0.06)', borderBottom: '1px solid rgba(167,139,250,0.12)' }}>
        <div className="w-2.5 h-2.5 rounded-full" style={{ background: '#ff5f57' }} />
        <div className="w-2.5 h-2.5 rounded-full" style={{ background: '#febc2e' }} />
        <div className="w-2.5 h-2.5 rounded-full" style={{ background: '#28c840' }} />
        <span className="ml-2 text-xs font-medium" style={{ color: 'rgba(167,139,250,0.70)', fontFamily: 'JetBrains Mono, monospace' }}>AI Command Center — 10:30 AM</span>
      </div>
      <div className="p-5 space-y-3">
        <div className="rounded-xl p-3" style={{ background: 'rgba(167,139,250,0.07)', border: '1px solid rgba(167,139,250,0.15)' }}>
          <div className="flex items-center gap-2 mb-1.5">
            <div className="w-5 h-5 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #A78BFA, #7C3AED)' }}>
              <Mic size={9} className="text-white" />
            </div>
            <span className="text-xs italic" style={{ color: 'rgba(240,244,255,0.55)' }}>"Who should I call next?"</span>
          </div>
        </div>
        {[
          { name: 'Marcus Webb', reason: 'Viewed 3 listings last night. No response in 8 days. High intent signal.', confidence: 94, action: 'Call now', color: '#00D4FF' },
          { name: 'Jennifer & Tom Peterson', reason: 'Offer expires in 6 hours. Waiting on your response.', confidence: 88, action: 'Review offer', color: '#F59E0B' },
          { name: 'David Kim', reason: 'Pre-approved last week. Hasn\'t scheduled a showing yet.', confidence: 71, action: 'Send listings', color: '#A78BFA' },
        ].map((lead, i) => (
          <div key={lead.name} className="rounded-xl p-3.5" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
            <div className="flex items-start justify-between gap-3 mb-2">
              <div>
                <div className="text-sm font-bold mb-0.5" style={{ color: '#F0F4FF' }}>{lead.name}</div>
                <div className="text-xs leading-relaxed" style={{ color: 'rgba(240,244,255,0.55)' }}>{lead.reason}</div>
              </div>
              <div className="flex-shrink-0 text-right">
                <div className="text-lg font-extrabold" style={{ color: lead.color, fontFamily: 'JetBrains Mono, monospace' }}>{lead.confidence}%</div>
                <div className="text-xs" style={{ color: 'rgba(240,244,255,0.35)' }}>confidence</div>
              </div>
            </div>
            <button className="text-xs font-semibold px-3 py-1.5 rounded-lg transition-all" style={{ background: `${lead.color}18`, color: lead.color, border: `1px solid ${lead.color}30` }}>
              {lead.action} →
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function CatchMeUpMockup() {
  return (
    <div className="rounded-2xl overflow-hidden" style={{ background: '#0B0F19', border: '1px solid rgba(34,197,94,0.20)', boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }}>
      <div className="flex items-center gap-2 px-4 py-3" style={{ background: 'rgba(34,197,94,0.06)', borderBottom: '1px solid rgba(34,197,94,0.12)' }}>
        <div className="w-2.5 h-2.5 rounded-full" style={{ background: '#ff5f57' }} />
        <div className="w-2.5 h-2.5 rounded-full" style={{ background: '#febc2e' }} />
        <div className="w-2.5 h-2.5 rounded-full" style={{ background: '#28c840' }} />
        <span className="ml-2 text-xs font-medium" style={{ color: 'rgba(34,197,94,0.70)', fontFamily: 'JetBrains Mono, monospace' }}>Catch Me Up — 12:30 PM</span>
      </div>
      <div className="p-5 space-y-3">
        <div className="rounded-xl p-4" style={{ background: 'rgba(34,197,94,0.07)', border: '1px solid rgba(34,197,94,0.15)' }}>
          <div className="flex items-center gap-2 mb-2">
            <RefreshCw size={13} style={{ color: '#22C55E' }} />
            <span className="text-xs font-bold" style={{ color: '#22C55E', fontFamily: 'JetBrains Mono, monospace' }}>SINCE 7:00 AM</span>
          </div>
          <p className="text-xs leading-relaxed" style={{ color: 'rgba(240,244,255,0.75)' }}>
            Here's what happened while you were at your showings.
          </p>
        </div>
        {[
          { icon: MessageSquare, label: '3 email replies received', color: '#00D4FF', detail: 'Marcus Webb replied. The Garcias confirmed Saturday.' },
          { icon: Bell, label: '2 new listings in your target area', color: '#A78BFA', detail: '14 Oak Lane and 88 River Rd — both match buyer criteria.' },
          { icon: TrendingUp, label: 'Buyer activity spike', color: '#22C55E', detail: 'David Kim viewed 5 listings in the last 2 hours.' },
          { icon: CheckCircle, label: '4 tasks completed', color: '#F59E0B', detail: 'Follow-up emails sent, offer reviewed, calendar updated.' },
        ].map((item) => (
          <div key={item.label} className="flex items-start gap-3 rounded-xl p-3" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)' }}>
            <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${item.color}18`, border: `1px solid ${item.color}30` }}>
              <item.icon size={13} style={{ color: item.color }} />
            </div>
            <div>
              <div className="text-xs font-semibold mb-0.5" style={{ color: '#F0F4FF' }}>{item.label}</div>
              <div className="text-xs" style={{ color: 'rgba(240,244,255,0.45)' }}>{item.detail}</div>
            </div>
          </div>
        ))}
        <div className="rounded-xl p-3.5" style={{ background: 'rgba(0,212,255,0.07)', border: '1px solid rgba(0,212,255,0.15)' }}>
          <div className="text-xs font-bold mb-1.5" style={{ color: '#00D4FF', fontFamily: 'JetBrains Mono, monospace' }}>3 THINGS NEEDING YOUR ATTENTION</div>
          <div className="space-y-1">
            {['Call Marcus Webb — he replied and is ready to move', 'Review the 14 Oak Lane listing for the Garcias', 'David Kim is hot — send him a curated shortlist'].map((item, i) => (
              <div key={i} className="flex items-start gap-2">
                <span className="text-xs font-bold mt-0.5" style={{ color: '#00D4FF', fontFamily: 'JetBrains Mono, monospace' }}>{i + 1}.</span>
                <span className="text-xs" style={{ color: 'rgba(240,244,255,0.70)' }}>{item}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function MarketingMockup() {
  return (
    <div className="rounded-2xl overflow-hidden" style={{ background: '#0B0F19', border: '1px solid rgba(245,158,11,0.20)', boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }}>
      <div className="flex items-center gap-2 px-4 py-3" style={{ background: 'rgba(245,158,11,0.06)', borderBottom: '1px solid rgba(245,158,11,0.12)' }}>
        <div className="w-2.5 h-2.5 rounded-full" style={{ background: '#ff5f57' }} />
        <div className="w-2.5 h-2.5 rounded-full" style={{ background: '#febc2e' }} />
        <div className="w-2.5 h-2.5 rounded-full" style={{ background: '#28c840' }} />
        <span className="ml-2 text-xs font-medium" style={{ color: 'rgba(245,158,11,0.70)', fontFamily: 'JetBrains Mono, monospace' }}>Marketing — 2:30 PM</span>
      </div>
      <div className="p-5 space-y-3">
        <div className="rounded-xl p-3" style={{ background: 'rgba(245,158,11,0.07)', border: '1px solid rgba(245,158,11,0.15)' }}>
          <div className="flex items-center gap-2 mb-1.5">
            <div className="w-5 h-5 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #F59E0B, #D97706)' }}>
              <Mic size={9} className="text-black" />
            </div>
            <span className="text-xs italic" style={{ color: 'rgba(240,244,255,0.55)' }}>"Create today's social post."</span>
          </div>
        </div>
        <div className="rounded-xl p-4" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
          <div className="text-xs font-bold mb-2" style={{ color: 'rgba(240,244,255,0.40)', fontFamily: 'JetBrains Mono, monospace' }}>GENERATED POST — INSTAGRAM</div>
          <p className="text-sm leading-relaxed mb-3" style={{ color: 'rgba(240,244,255,0.85)' }}>
            Just listed: 14 Oak Lane 🏡<br /><br />
            3 bed · 2 bath · 1,850 sq ft<br />
            Quiet cul-de-sac, updated kitchen, and a backyard made for summer.<br /><br />
            Priced at $485,000 — and it won't last.<br /><br />
            DM me to schedule a private showing this weekend. 🔑<br /><br />
            #RealEstate #JustListed #OakLane #YourCityHomes
          </p>
          <div className="flex gap-2">
            <button className="flex-1 text-xs font-bold py-2 rounded-lg" style={{ background: 'linear-gradient(135deg, #F59E0B, #D97706)', color: '#0B0F19' }}>
              ✓ Approve & Post
            </button>
            <button className="text-xs font-semibold px-3 py-2 rounded-lg" style={{ background: 'rgba(255,255,255,0.07)', color: 'rgba(240,244,255,0.55)', border: '1px solid rgba(255,255,255,0.10)' }}>
              Edit
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function DashboardMockup() {
  return (
    <div className="rounded-2xl overflow-hidden" style={{ background: '#0B0F19', border: '1px solid rgba(0,212,255,0.20)', boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }}>
      <div className="flex items-center gap-2 px-4 py-3" style={{ background: 'rgba(0,212,255,0.06)', borderBottom: '1px solid rgba(0,212,255,0.12)' }}>
        <div className="w-2.5 h-2.5 rounded-full" style={{ background: '#ff5f57' }} />
        <div className="w-2.5 h-2.5 rounded-full" style={{ background: '#febc2e' }} />
        <div className="w-2.5 h-2.5 rounded-full" style={{ background: '#28c840' }} />
        <span className="ml-2 text-xs font-medium" style={{ color: 'rgba(0,212,255,0.70)', fontFamily: 'JetBrains Mono, monospace' }}>Business Dashboard — 4:30 PM</span>
        <div className="ml-auto flex items-center gap-1 rounded-full px-2 py-0.5" style={{ background: 'rgba(0,212,255,0.12)', border: '1px solid rgba(0,212,255,0.20)' }}>
          <Zap size={9} style={{ color: '#00D4FF' }} />
          <span className="text-xs font-bold" style={{ color: '#00D4FF', fontFamily: 'JetBrains Mono, monospace' }}>AI View</span>
        </div>
      </div>
      <div className="p-5 space-y-3">
        <div className="grid grid-cols-2 gap-2">
          {[
            { label: 'Active pipeline', value: '$2.4M', change: '+12%', color: '#00D4FF' },
            { label: 'Deals this month', value: '3', change: '+1 this week', color: '#22C55E' },
            { label: 'Follow-up rate', value: '94%', change: 'vs 71% avg', color: '#A78BFA' },
            { label: 'Response time', value: '18 min', change: 'industry avg: 4h', color: '#F59E0B' },
          ].map((stat) => (
            <div key={stat.label} className="rounded-xl p-3.5" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)' }}>
              <div className="text-xl font-extrabold mb-0.5" style={{ color: stat.color, fontFamily: 'JetBrains Mono, monospace' }}>{stat.value}</div>
              <div className="text-xs mb-1" style={{ color: 'rgba(240,244,255,0.45)' }}>{stat.label}</div>
              <div className="text-xs font-semibold" style={{ color: '#22C55E' }}>{stat.change}</div>
            </div>
          ))}
        </div>
        <div className="rounded-xl p-3.5" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
          <div className="text-xs font-bold mb-2" style={{ color: 'rgba(240,244,255,0.40)', fontFamily: 'JetBrains Mono, monospace' }}>PIPELINE HEALTH</div>
          <div className="space-y-2">
            {[
              { stage: 'New leads', count: 8, pct: 80, color: '#00D4FF' },
              { stage: 'Active buyers', count: 5, pct: 50, color: '#A78BFA' },
              { stage: 'Under contract', count: 2, pct: 20, color: '#22C55E' },
            ].map((item) => (
              <div key={item.stage}>
                <div className="flex justify-between mb-1">
                  <span className="text-xs" style={{ color: 'rgba(240,244,255,0.55)' }}>{item.stage}</span>
                  <span className="text-xs font-bold" style={{ color: item.color, fontFamily: 'JetBrains Mono, monospace' }}>{item.count}</span>
                </div>
                <div className="h-1.5 rounded-full" style={{ background: 'rgba(255,255,255,0.08)' }}>
                  <div className="h-full rounded-full" style={{ width: `${item.pct}%`, background: item.color }} />
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="flex gap-2">
          <button className="flex-1 text-xs font-bold py-2 rounded-lg" style={{ background: 'linear-gradient(135deg, #00D4FF, #0099CC)', color: '#0B0F19' }}>
            AI View
          </button>
          <button className="flex-1 text-xs font-semibold py-2 rounded-lg" style={{ background: 'rgba(255,255,255,0.07)', color: 'rgba(240,244,255,0.55)', border: '1px solid rgba(255,255,255,0.10)' }}>
            Traditional View
          </button>
        </div>
      </div>
    </div>
  );
}

function WrapUpMockup() {
  return (
    <div className="rounded-2xl overflow-hidden" style={{ background: '#0B0F19', border: '1px solid rgba(167,139,250,0.20)', boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }}>
      <div className="flex items-center gap-2 px-4 py-3" style={{ background: 'rgba(167,139,250,0.06)', borderBottom: '1px solid rgba(167,139,250,0.12)' }}>
        <div className="w-2.5 h-2.5 rounded-full" style={{ background: '#ff5f57' }} />
        <div className="w-2.5 h-2.5 rounded-full" style={{ background: '#febc2e' }} />
        <div className="w-2.5 h-2.5 rounded-full" style={{ background: '#28c840' }} />
        <span className="ml-2 text-xs font-medium" style={{ color: 'rgba(167,139,250,0.70)', fontFamily: 'JetBrains Mono, monospace' }}>Wrap Up — 6:00 PM</span>
      </div>
      <div className="p-5 space-y-3">
        <div className="rounded-xl p-4" style={{ background: 'rgba(167,139,250,0.07)', border: '1px solid rgba(167,139,250,0.15)' }}>
          <div className="flex items-center gap-2 mb-2">
            <Moon size={13} style={{ color: '#A78BFA' }} />
            <span className="text-xs font-bold" style={{ color: '#A78BFA', fontFamily: 'JetBrains Mono, monospace' }}>TODAY'S SUMMARY</span>
          </div>
          <p className="text-xs leading-relaxed" style={{ color: 'rgba(240,244,255,0.75)' }}>
            Strong day. You advanced 2 deals, completed 7 follow-ups, and responded to every lead within 20 minutes. Marcus Webb is ready to write an offer.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-2">
          {[
            { label: 'Tasks completed', value: '11', color: '#22C55E' },
            { label: 'Deals advanced', value: '2', color: '#00D4FF' },
            { label: 'Emails sent', value: '8', color: '#A78BFA' },
            { label: 'Calls made', value: '5', color: '#F59E0B' },
          ].map((stat) => (
            <div key={stat.label} className="rounded-lg p-3" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)' }}>
              <div className="text-lg font-extrabold mb-0.5" style={{ color: stat.color, fontFamily: 'JetBrains Mono, monospace' }}>{stat.value}</div>
              <div className="text-xs" style={{ color: 'rgba(240,244,255,0.45)' }}>{stat.label}</div>
            </div>
          ))}
        </div>
        <div className="rounded-xl p-3.5" style={{ background: 'rgba(0,212,255,0.07)', border: '1px solid rgba(0,212,255,0.15)' }}>
          <div className="text-xs font-bold mb-2" style={{ color: '#00D4FF', fontFamily: 'JetBrains Mono, monospace' }}>FIRST TASK TOMORROW</div>
          <div className="flex items-start gap-2">
            <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: 'linear-gradient(135deg, #00D4FF, #0099CC)' }}>
              <span className="text-xs font-bold text-black">1</span>
            </div>
            <p className="text-xs leading-relaxed" style={{ color: 'rgba(240,244,255,0.75)' }}>
              Call Marcus Webb at 9 AM. He's ready to write an offer on 14 Oak Lane. Draft is prepared and waiting.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function getMockup(type: string) {
  switch (type) {
    case 'morning-brief': return <MorningBriefMockup />;
    case 'who-to-call': return <WhoToCallMockup />;
    case 'catch-me-up': return <CatchMeUpMockup />;
    case 'marketing': return <MarketingMockup />;
    case 'dashboard': return <DashboardMockup />;
    case 'wrap-up': return <WrapUpMockup />;
    default: return null;
  }
}

export default function InActionPage() {
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.08, rootMargin: '0px 0px -40px 0px' }
    );
    const elements = document.querySelectorAll('.animate-on-scroll');
    elements?.forEach((el) => observerRef?.current?.observe(el));
    return () => observerRef?.current?.disconnect();
  }, []);

  return (
    <main className="min-h-screen font-jakarta" style={{ background: '#0B0F19' }}>
      <Navbar />

      {/* Hero */}
      <section className="hero-bg pt-28 pb-20 relative overflow-hidden">
        <div className="absolute top-1/3 left-1/4 w-[600px] h-[600px] rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(0,212,255,0.07) 0%, transparent 70%)', filter: 'blur(60px)' }} />
        <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(167,139,250,0.06) 0%, transparent 70%)', filter: 'blur(50px)' }} />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="section-badge mb-6">
            <Play size={11} />
            See It In Action
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.1] tracking-tight mb-6" style={{ color: '#F0F4FF' }}>
            See AgentOS{' '}
            <span className="gradient-cyan-lavender-text">in Action.</span>
          </h1>
          <p className="text-lg sm:text-xl leading-relaxed max-w-2xl mx-auto mb-10" style={{ color: 'rgba(240,244,255,0.60)' }}>
            Follow a typical Realtor's day and see how AgentOS becomes your AI Business Partner from morning to night.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <a href="https://agentos-ai-vgojf93.public.builtwithrocket.new/" target="_blank" rel="noopener noreferrer" className="btn-primary inline-flex items-center gap-2 text-base px-7 py-3.5">
              Start Free Trial
              <ArrowRight size={16} />
            </a>
            <a href="#contact" onClick={(e) => { e.preventDefault(); document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' }); }} className="btn-secondary inline-flex items-center gap-2 text-base px-7 py-3.5">
              Schedule Live Demo
            </a>
          </div>
        </div>
      </section>

      {/* Timeline Sections */}
      <div className="relative">
        {/* Vertical timeline line */}
        <div className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-px -translate-x-1/2 pointer-events-none" style={{ background: 'linear-gradient(180deg, transparent, rgba(0,212,255,0.15) 10%, rgba(167,139,250,0.15) 50%, rgba(0,212,255,0.15) 90%, transparent)' }} />

        {timelineItems.map((item, index) => {
          const isEven = index % 2 === 0;
          return (
            <section
              key={item.time}
              className={`py-20 relative ${index % 2 === 0 ? '' : 'section-alt-bg'}`}
            >
              <div className="absolute inset-0 pointer-events-none" style={{ background: `radial-gradient(ellipse 50% 60% at ${isEven ? '20%' : '80%'} 50%, ${item.glow} 0%, transparent 70%)` }} />

              {/* Timeline dot */}
              <div className="hidden lg:flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full items-center justify-center" style={{ background: item.gradient, boxShadow: `0 0 0 4px #0B0F19, 0 0 0 6px ${item.color}40` }}>
                <item.icon size={18} className="text-white" strokeWidth={2} />
              </div>

              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
                <div className={`grid lg:grid-cols-2 gap-12 lg:gap-20 items-center ${isEven ? '' : 'lg:flex lg:flex-row-reverse'}`}>
                  {/* Content */}
                  <div className={`animate-on-scroll ${isEven ? 'lg:pr-16' : 'lg:pl-16'}`}>
                    <div className="flex items-center gap-3 mb-5">
                      <div className="flex items-center gap-2 px-3 py-1.5 rounded-full" style={{ background: `${item.color}15`, border: `1px solid ${item.color}30` }}>
                        <Clock size={11} style={{ color: item.color }} />
                        <span className="text-xs font-bold" style={{ color: item.color, fontFamily: 'JetBrains Mono, monospace' }}>{item.time}</span>
                      </div>
                      <span className="text-2xl">{item.emoji}</span>
                    </div>

                    <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-4 leading-tight" style={{ color: '#F0F4FF' }}>
                      {item.title}
                    </h2>

                    <p className="text-lg font-medium mb-3 leading-relaxed" style={{ color: 'rgba(240,244,255,0.80)' }}>
                      {item.story}
                    </p>
                    <p className="text-base leading-relaxed mb-6" style={{ color: 'rgba(240,244,255,0.55)' }}>
                      {item.detail}
                    </p>

                    <ul className="space-y-2.5">
                      {item.bullets.map((bullet) => (
                        <li key={bullet} className="flex items-start gap-3">
                          <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: `${item.color}18`, border: `1px solid ${item.color}35` }}>
                            <CheckCircle size={11} style={{ color: item.color }} />
                          </div>
                          <span className="text-sm leading-relaxed" style={{ color: 'rgba(240,244,255,0.70)' }}>{bullet}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Mockup */}
                  <div className={`animate-on-scroll ${isEven ? 'lg:pl-16' : 'lg:pr-16'}`} style={{ animationDelay: '0.15s' }}>
                    {getMockup(item.mockup.type)}
                  </div>
                </div>
              </div>
            </section>
          );
        })}
      </div>

      {/* Ending CTA Section */}
      <section className="py-28 relative overflow-hidden" style={{ background: '#0B0F19' }}>
        <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse 70% 60% at 50% 50%, rgba(0,212,255,0.07) 0%, transparent 70%)' }} />
        <div className="absolute top-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(0,212,255,0.20), rgba(167,139,250,0.20), transparent)' }} />

        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="section-badge mb-8">
            <Sparkles size={12} />
            Your AI Business Partner
          </div>

          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mb-8 leading-[1.1]" style={{ color: '#F0F4FF' }}>
            Imagine every workday{' '}
            <span className="gradient-cyan-lavender-text">feeling like this.</span>
          </h2>

          <div className="grid sm:grid-cols-2 gap-4 mb-12 text-left max-w-xl mx-auto">
            <div>
              <div className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: 'rgba(240,244,255,0.30)', fontFamily: 'JetBrains Mono, monospace' }}>Less of this</div>
              {['Stress', 'Forgotten follow-ups', 'Busy work', 'Missed opportunities'].map((item) => (
                <div key={item} className="flex items-center gap-2 mb-2">
                  <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: 'rgba(240,244,255,0.25)' }} />
                  <span className="text-sm" style={{ color: 'rgba(240,244,255,0.45)' }}>{item}</span>
                </div>
              ))}
            </div>
            <div>
              <div className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: '#00D4FF', fontFamily: 'JetBrains Mono, monospace' }}>More of this</div>
              {['Conversations', 'Relationships', 'Closings', 'Clarity'].map((item) => (
                <div key={item} className="flex items-center gap-2 mb-2">
                  <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: '#00D4FF' }} />
                  <span className="text-sm font-medium" style={{ color: 'rgba(240,244,255,0.75)' }}>{item}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap gap-4 justify-center mb-6">
            <a href="https://agentos-ai-vgojf93.public.builtwithrocket.new/" target="_blank" rel="noopener noreferrer" className="btn-primary inline-flex items-center gap-2 text-base px-8 py-4">
              Start Free Trial
              <ArrowRight size={16} />
            </a>
            <Link href="/#contact" className="btn-secondary inline-flex items-center gap-2 text-base px-8 py-4">
              Schedule Live Demo
            </Link>
          </div>
          <p className="text-sm" style={{ color: 'rgba(240,244,255,0.30)' }}>No credit card required. Cancel anytime.</p>
        </div>
      </section>

      <Footer />
    </main>
  );
}
